import type { CardFormat } from "./types";
import type { ResolvedTheme } from "./theme";

/**
 * Renders the theme's decorative overlay (if any) absolutely-positioned inside
 * the card. Sits above the FbkCardShell base but below the card content.
 *
 * Card components include this just inside the FbkCardShell:
 *   <FbkCardShell ...>
 *     <ThemeOverlay theme={theme} format={format} />
 *     <div style={{ position: "relative", zIndex: 2 }}>...content...</div>
 *   </FbkCardShell>
 */
export function ThemeOverlay({
  theme,
  format,
}: {
  theme: ResolvedTheme;
  format: CardFormat;
}) {
  const isStory = format === "story";

  if (theme.decorativeOverlay === "spotlight-halo") {
    return (
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background:
            "radial-gradient(circle at 50% 48%, rgba(255, 215, 0, 0.32) 0%, rgba(255, 215, 0, 0.12) 25%, transparent 55%)",
          zIndex: 1,
        }}
      />
    );
  }

  if (theme.decorativeOverlay === "halftone-dots") {
    return (
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: 1,
          opacity: 0.5,
          backgroundImage:
            "radial-gradient(circle, #000 1.5px, transparent 2px)",
          backgroundSize: "14px 14px",
          mixBlendMode: "multiply",
        }}
      />
    );
  }

  if (theme.decorativeOverlay === "editorial-rules") {
    return (
      <>
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: isStory ? 48 : 32,
            left: isStory ? 80 : 56,
            right: isStory ? 80 : 56,
            height: 1,
            background: `${theme.quoteBarColor}66`, // ~40% opacity
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
        <div
          aria-hidden
          style={{
            position: "absolute",
            bottom: isStory ? 48 : 32,
            left: isStory ? 80 : 56,
            right: isStory ? 80 : 56,
            height: 1,
            background: `${theme.quoteBarColor}66`,
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
      </>
    );
  }

  // ─── KNOCKOUT ── faint tournament bracket "V" angles tile the background ──
  // A repeating SVG drawn at low opacity. Reads as "knockout stage" without
  // overwhelming the headline.
  if (theme.decorativeOverlay === "knockout-brackets") {
    const tileSize = isStory ? 220 : 180;
    // Two angle brackets per tile, alternating direction — looks like a
    // mini bracket diagram repeated across the canvas.
    const svg = encodeURIComponent(
      `<svg xmlns='http://www.w3.org/2000/svg' width='${tileSize}' height='${tileSize}' viewBox='0 0 ${tileSize} ${tileSize}'>
        <g fill='none' stroke='#FFD700' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>
          <polyline points='${tileSize * 0.1},${tileSize * 0.2} ${tileSize * 0.3},${tileSize * 0.5} ${tileSize * 0.1},${tileSize * 0.8}'/>
          <polyline points='${tileSize * 0.9},${tileSize * 0.2} ${tileSize * 0.7},${tileSize * 0.5} ${tileSize * 0.9},${tileSize * 0.8}'/>
        </g>
      </svg>`,
    );
    return (
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: 1,
          opacity: 0.08,
          backgroundImage: `url("data:image/svg+xml;utf8,${svg}")`,
          backgroundSize: `${tileSize}px ${tileSize}px`,
          backgroundRepeat: "repeat",
        }}
      />
    );
  }

  // ─── TROPHY ── corner sparkle "star" glints (4 of them) + center halo ────
  // Layered radial gradients give a glittering trophy-room feel.
  if (theme.decorativeOverlay === "trophy-sparkle") {
    const sparkleSize = isStory ? 140 : 100;
    return (
      <>
        {/* Center light beam from above */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            zIndex: 1,
            background:
              "radial-gradient(ellipse 35% 80% at 50% 30%, rgba(255,215,0,0.22) 0%, transparent 65%)",
          }}
        />
        {/* Four corner sparkle glints */}
        {[
          { top: "8%", left: "10%" },
          { top: "12%", right: "12%" },
          { bottom: "18%", left: "8%" },
          { bottom: "10%", right: "10%" },
        ].map((pos, i) => (
          <div
            key={i}
            aria-hidden
            style={{
              position: "absolute",
              ...pos,
              width: sparkleSize,
              height: sparkleSize,
              pointerEvents: "none",
              zIndex: 1,
              background:
                "radial-gradient(circle, rgba(255,225,122,0.55) 0%, rgba(255,215,0,0.18) 25%, transparent 60%)",
              filter: "blur(2px)",
            }}
          />
        ))}
      </>
    );
  }

  // ─── ANTHEM ── horizontal light rays converging on the center band ──────
  // Suggests stadium PA / ceremonial moment without being noisy.
  if (theme.decorativeOverlay === "anthem-rays") {
    return (
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: 1,
          background:
            "radial-gradient(ellipse 60% 25% at 50% 50%, rgba(255,215,0,0.18) 0%, transparent 70%)",
        }}
      />
    );
  }

  // ─── EMERALD NOIR ── gold seam ring hugging a centered hero rectangle ──
  // Thin gold border ~80% of card width, centered, with a soft inner glow.
  // Reads as a broadcast title-card frame.
  if (theme.decorativeOverlay === "emerald-noir-ring") {
    const inset = isStory ? "10%" : "8%";
    return (
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset,
          pointerEvents: "none",
          zIndex: 1,
          border: "1.5px solid rgba(255,215,0,0.55)",
          borderRadius: isStory ? 28 : 22,
          boxShadow:
            "inset 0 0 60px rgba(255,215,0,0.10), 0 0 30px rgba(255,215,0,0.15)",
        }}
      />
    );
  }

  return null;
}
